/**
 * A CARD'S EFFECTIVE PROGRESS — the one number every surface should read.
 *
 * A card's percent-complete can come from three places, in strict precedence:
 *
 *   1. MANUAL — a person (the doer or a manager) typed a %. An explicit statement,
 *      it always wins.
 *   2. CARD-CHECKLIST — nobody typed a %, but the card has its OWN checklist
 *      (issue.checklist, the per-card list of tickable steps) with items on it.
 *      Then the % is DERIVED from how much of THAT list is ticked: 2 of 4 done =
 *      50%. The card's own steps are the truest read of how far it is, so they win
 *      over the board's shared Definition of Done.
 *   3. DONE-CHECKS — no typed %, no card checklist, but the board has a Definition
 *      of Done (the shared list set in Settings › Workflow) and this card has
 *      ticked some of it. The % is derived from that list — today's behaviour,
 *      unchanged. The DoD still shows as the readiness gate on a card that also
 *      has its own checklist; it just no longer drives the number.
 *
 * When none is present the card has no progress to show (null) — it is not "0%",
 * it is "unmeasured", and the health read keeps treating it as such. An EMPTY card
 * checklist (length 0) is "no checklist" and falls through to the DoD.
 *
 * Each derivation's denominator is the WHOLE list — the card checklist's own
 * length, or the board's full DoD list length — never the count of keys that
 * happen to sit on the card. Pure: no store, no React — unit-tested in
 * scripts/test-progress-derive.ts.
 */
import type { Issue } from './types';

/** Where a shown % came from — so a surface can say where it is derived from.
 *  'card-checklist' = the card's own checklist; 'done-checks' = the board DoD. */
export type ProgressSource = 'manual' | 'card-checklist' | 'done-checks' | null;

export interface EffectiveProgress {
  /** 0..100, or null when the card has neither a typed % nor any DoD to derive from. */
  pct: number | null;
  source: ProgressSource;
}

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

/**
 * The card's progress and where it came from. `doneChecks` is the board's DoD
 * list (ProjectMeta.doneChecks) — pass it wherever the board meta is in scope so
 * a card with no typed % and no checklist still contributes its ticked-off share;
 * omit it (or pass an empty list) where the board has no definition of done and
 * only a typed % or the card's own checklist counts.
 */
export function effectiveProgress(
  issue: Pick<Issue, 'progressPct' | 'checks' | 'checklist'>,
  doneChecks?: readonly string[] | null,
): EffectiveProgress {
  const raw = issue.progressPct;
  if (raw != null && !Number.isNaN(raw)) {
    return { pct: clamp(Math.round(raw), 0, 100), source: 'manual' };
  }
  // The card's OWN checklist wins over the board DoD — its steps are the truest
  // read of this card. An empty list is "no checklist" and falls through.
  const list = issue.checklist;
  if (list && list.length > 0) {
    const done = list.reduce((n, it) => n + (it.done ? 1 : 0), 0);
    return { pct: Math.round((done / list.length) * 100), source: 'card-checklist' };
  }
  const required = doneChecks ?? [];
  if (required.length > 0) {
    const done = required.reduce((n, c) => n + (issue.checks?.[c] === true ? 1 : 0), 0);
    return { pct: Math.round((done / required.length) * 100), source: 'done-checks' };
  }
  return { pct: null, source: null };
}

/** Just the number (or null) — for callers that don't need the provenance. */
export function effectiveProgressPct(
  issue: Pick<Issue, 'progressPct' | 'checks' | 'checklist'>,
  doneChecks?: readonly string[] | null,
): number | null {
  return effectiveProgress(issue, doneChecks).pct;
}
